import {AttrsSchema, Component, ConfigOptions} from 'panel';
import isEqual from 'lodash/isEqual';
import {Point} from 'highcharts';

import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController, SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {ChartLegendTheme} from 'common/components/chart-legend/constants';
import {ColorChoice, DateKeyedFunnelSegments, FunnelBookmark, TrendChartType} from 'common/report/funnels/models/types';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {
  createFunnelDataWithRename,
  getSegmentColumnSpecs,
  getSelectedSegments,
  hasData,
  orderSegmentsbyTotalConversions,
} from 'common/report/funnels/util';
import {LineChartAttrs} from 'common/charts/visualization/line-chart';
import {DisplayAnalysisType} from 'common/charts/visualization/line-chart/enums';
import {ChartType, PropertyType, REPORT_LIMITS, ReportName, SavedLegend} from 'common/report/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {processFunnelForChartWidget} from 'common/report/funnels/models/aggregates';
import {getMedianAccessor, getTTCShiftedEventIndex} from 'common/report/funnels/models/time-to-convert/utils';
import {getTrendDataTableColumnGroups, getTrendDataTableRow} from 'common/components/data-table/trend-data/utils';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {formatTrendValue, TrendValueFormat} from 'common/report/funnels/models/trends';
import {getLiftBaselineSegment, getLiftComparisonValues} from 'common/report/util/lift';
import {getBookmarkWithAppliedDefaults} from 'common/report/funnels/util/bookmark-overrides';
import {CurrentProject, CurrentProjectResource} from 'common/report/model-resources/projects';
import {PopoverCallback} from 'common/charts/types';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {deselectPoint, selectPoint} from 'common/report/util/highcharts';
import {parseDatetimeRange} from 'common/util/date';

import {formatTooltip, formatYAxisLabel, getChartData, getTrendDataRows} from './formatters';
import {template} from './template';
import css from './index.inline.styl';

interface FunnelsTrendsProjectDetails {
  projectTimeZone: string;
  projectAsOfDate: null | string;
  utcOffset: number;
  projectId: string;
  timePeriodSettings: null | {
    weekStartDay: number;
    quarterStartMonth: number;
  };
}

interface FunnelsTrendsChartJsxProps {
  props: {
    bookmark: FunnelBookmark;
    funnelsResponse: FunnelQueryResponse;
    projectDetails?: FunnelsTrendsProjectDetails | null;
    popoverCallback?: PopoverCallback;
  };
}
interface FunnelsTrendState {
  liftComparisonData: DateKeyedFunnelSegments[];
  comparisonSegmentData: DateKeyedFunnelSegments[];
  segmentData: DateKeyedFunnelSegments[];
  query: FunnelsQuery;
  popoverStyles?: {
    visibility?: string;
    left?: string;
    top?: string;
  } | null;
  popoverSelection: Point | null;
}
interface ChartAttrs {
  'show-annotations': boolean;
}
export class FunnelsTrendsChart extends Component<FunnelsTrendState, ChartAttrs, any, any, ChartsContextRegistry> {
  private _funnelsResponse: FunnelQueryResponse;
  private _bookmark: FunnelBookmark;
  private _projectDetails: FunnelsTrendsProjectDetails | null = null;

  static get attrsSchema(): AttrsSchema<ChartAttrs> {
    return {
      'show-annotations': {
        type: `boolean`,
        description: `shows global annotations on mp-line-chart`,
      },
    };
  }

  get config(): ConfigOptions<FunnelsTrendState, any, ChartsContextRegistry> {
    return {
      css,
      contexts: [`segmentFilterController` as const, `currentProject` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      template,
      useShadowDom: true,
      helpers: {
        ChartLegendTheme,

        passThrough: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(ev.type, ev));
        },

        getLineChartProps: () => {
          return {
            chartData: this.helpers.getChartData(),
            formatYAxisLabel: this.helpers.formatYAxisLabel,
            formatTooltip: this.helpers.formatTooltip,
            onHighChartClick: this.popoverCallback && this.handlePointClick,
          };
        },

        getLineChartAttrs: (): Partial<LineChartAttrs> => {
          const projectDetails = this.getProjectDetails();
          const incompleteBuckets = this.getIncompleteBuckets();
          return {
            'chart-width': null,
            'display-analysis-type': DisplayAnalysisType.Linear,
            'incomplete-from-date': incompleteBuckets.first,
            'incomplete-to-date': incompleteBuckets.last,
            'show-annotations': this.attr(`show-annotations`),
            timezone: projectDetails.projectTimeZone,
            'utc-offset': projectDetails.utcOffset,
            'zoom-type': `x`,
            'time-unit': this.helpers.getTrendUnit(),
          };
        },

        hasNoData: () => !hasData(this.state.segmentData),

        getChartData: () => {
          return getChartData({
            comparedTrends: this.state.comparisonSegmentData,
            eventIndex: this.shiftedEventIndex(),
            funnelsQuery: this.state.query,
            orderedTrends: this.state.segmentData,
            trendType: this._bookmark.trendType,
            segmentFilterController: this.segmentFilterController,
            liftComparisonTrends: this.state.liftComparisonData,
          });
        },

        getTrendUnit: () => this.state.query.trendUnit,

        formatYAxisLabel: (value: number) =>
          formatYAxisLabel(value, this._bookmark.trendType, this._bookmark.liftComparison?.type),

        formatTooltip: (x: number, _y: number, series: any, _idx: number) =>
          formatTooltip({
            comparedTrends: this.state.comparisonSegmentData,
            liftTrends: this.state.liftComparisonData,
            eventIndex: this.shiftedEventIndex(),
            funnelsQuery: this.state.query,
            isCompact: false,
            orderedTrends: this.state.segmentData,
            trendType: this._bookmark.trendType,
            x,
            series,
          }),
      },
    };
  }

  public popoverCallback: PopoverCallback | null = null;

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  public set funnelsResponse(response: FunnelQueryResponse) {
    if (!isEqual(this._funnelsResponse, response)) {
      this._funnelsResponse = response;
      this.processData();
    }
  }

  public get funnelsResponse(): FunnelQueryResponse {
    return this._funnelsResponse;
  }

  public set bookmark(bookmark: FunnelBookmark) {
    const updatedBookmark = getBookmarkWithAppliedDefaults(bookmark);
    if (!isEqual(this._bookmark, updatedBookmark)) {
      this._bookmark = updatedBookmark;
      this.processData();
    }
  }

  public get bookmark(): FunnelBookmark {
    return this._bookmark;
  }

  public set projectDetails(value: FunnelsTrendsProjectDetails | null) {
    if (!isEqual(this._projectDetails, value)) {
      this._projectDetails = value;
      this.processData();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.processData();
  }

  resourceCustomSubscriptions = new Map([[CurrentProjectResource, () => !this._projectDetails && this.processData()]]);

  private getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  processData() {
    if (!this._bookmark || !this._funnelsResponse || !this.isConnected || !this.getProjectDetails()) {
      return;
    }

    const stepIndex = this.shiftedEventIndex();

    const projectDetails = this.getProjectDetails();

    const {legendSegmentItems, savedLegend, stateUpdates} = processFunnelForChartWidget(
      this._bookmark,
      this.funnelsResponse,
      projectDetails.projectId,
      projectDetails.projectAsOfDate,
      stepIndex,
      getMedianAccessor(stepIndex),
      projectDetails.projectTimeZone,
    );

    const {query, formattedData} = stateUpdates;

    const selectedSegments: ColorChoice[] = getSelectedSegments(
      this._bookmark.selectedSegments,
      this._bookmark.selectMode,
      formattedData,
      query.getCommonChartType(),
    );

    this.update({query});
    this.updateFunnelSegmentData();
    this.updateSegmentFilterController({legendSegmentItems, savedLegend, selectedSegments});
  }

  updateFunnelSegmentData() {
    const funnelQuery = this.state.query;

    // TODO: `processFunnelForChartWidget` computes this info also. Have `processFunnelForChartWidget` return
    // that info rather than recomputing it here. Note that the implementation in `processFunnelForChartWidget` calls
    // `getRenamedOrderedSegmentSteps` vs. `orderSegmentsbyTotalConversions` here for transforming the data. But these
    // implementations look quite similar and should be unifiable?
    const segmentData = getFunnelSegments(this._funnelsResponse?.data);
    const comparisonSegmentData = this._funnelsResponse?.compare_data
      ? getFunnelSegments(this._funnelsResponse?.compare_data)
      : null;
    const liftComparisonData = this._funnelsResponse?.lift_data
      ? getFunnelSegments(this._funnelsResponse?.lift_data)
      : null;

    this.update({segmentData, comparisonSegmentData, liftComparisonData});

    function getFunnelSegments(funnelData): DateKeyedFunnelSegments[] {
      const segmentData: DateKeyedFunnelSegments[] = createFunnelDataWithRename(funnelData, funnelQuery);
      return orderSegmentsbyTotalConversions(segmentData);
    }
  }

  getIncompleteBuckets() {
    return this.funnelsResponse.meta?.incomplete_buckets;
  }

  shiftedEventIndex() {
    return getTTCShiftedEventIndex(this._bookmark.eventIndex);
  }

  handlePointClick = (point: Point) => {
    const {series, plotX, plotY} = point;
    const segmentName = series.options.name;

    if (segmentName === OTHER_SEGMENT_VALUE) {
      return;
    }

    const hasPopoverActions = this.popoverCallback?.call(null, segmentName).length;

    if (hasPopoverActions) {
      selectPoint(point);

      const left = series.chart.plotLeft + plotX + 12;
      const top = series.chart.plotTop + plotY + 12;
      this.update({
        popoverStyles: {left: `${left}px`, top: `${top}px`},
        popoverSelection: point,
      });
    }
  };

  handlePopoverAction(ev: CustomEvent) {
    const {series, x: dateTime} = this.state.popoverSelection;
    const segmentName = series.options.name;
    const color = series.options.color as string;

    const {segmentData: orderedTrends} = this.state;
    const matchingTrendIndex = orderedTrends.findIndex((bucket) => dateTime === bucket.date.getTime());
    const matchingTrend = orderedTrends[matchingTrendIndex].segments.find(
      (segment) => segmentName === segment.propName,
    );
    const date = orderedTrends[matchingTrendIndex].date.getTime();
    const trendType = this._bookmark.trendType;
    let stepIndex;
    if (trendType === TrendChartType.TopOfFunnel) {
      stepIndex = this._bookmark.eventIndex || 0;
    } else {
      stepIndex = this.shiftedEventIndex() || matchingTrend.steps.length - 1;
    }
    const projectDetails = this.getProjectDetails();
    const quarterStartMonth = projectDetails.timePeriodSettings?.quarterStartMonth || 0;
    const unit = this.helpers.getTrendUnit();
    const dateRange = parseDatetimeRange(date, unit, quarterStartMonth);
    let from;
    let to;
    let type;
    if (unit === TimeUnit.Day) {
      // If we have a single date, it is both the `from` and `to` dates.
      from = dateRange;
      to = dateRange;
      type = DateRangeType.On;
    } else {
      // Otherwise we have a date range, so the `from` and `to` dates are in the array in sorted order.
      from = dateRange[0];
      to = dateRange[1];
      type = DateRangeType.Between;
    }

    this.dispatchEvent(
      new CustomEvent(`popoverAction`, {
        detail: {
          action: ev.detail.action,
          color,
          segment: matchingTrend.prop,
          stepIndex,
          isComparison: false,
          comparisonDateRange: null,
          selectedDateRange: {
            from,
            to,
            type,
          },
        },
      }),
    );
    this.handlePopoverClose();
  }

  handlePopoverClose() {
    const selectedPoint = this.state.popoverSelection;
    if (selectedPoint) {
      deselectPoint(selectedPoint);
    }
    this.update({
      popoverSelection: null,
      popoverStyles: null,
    });
  }

  private getProjectDetails(): FunnelsTrendsProjectDetails {
    if (this._projectDetails) {
      return this._projectDetails;
    }
    const currentProject = this.getCurrentProject();
    if (currentProject) {
      return {
        projectTimeZone: currentProject.timezone,
        projectAsOfDate: currentProject.projectAsOfDate,
        utcOffset: currentProject.utcOffset,
        projectId: currentProject.id.toString(),
        timePeriodSettings: currentProject.timePeriodSettings,
      };
    }
    return null;
  }

  private updateSegmentFilterController({
    legendSegmentItems,
    savedLegend,
    selectedSegments,
  }: {
    legendSegmentItems: SegmentItem[];
    savedLegend: SavedLegend[];
    selectedSegments: ColorChoice[];
  }) {
    if (!this.isConnected) {
      return;
    }

    const projectDetails = this.getProjectDetails();

    const trendDataRows = getTrendDataRows({
      comparedTrends: this.state.comparisonSegmentData,
      liftComparedTrends: this.state.liftComparisonData,
      eventIndex: this.shiftedEventIndex(),
      orderedTrends: this.state.segmentData,
      selected: selectedSegments,
      trendType: this._bookmark.trendType,
      liftType: this._bookmark.liftComparison?.type,
    });
    const trendDataRowLookup = new Map(trendDataRows.map((row) => [row.rowId, row]));
    const trendValueLookup = new Map(trendDataRows.map((row) => [row.rowId, row.data]));

    legendSegmentItems = legendSegmentItems.map((item) => {
      const segment = trendDataRowLookup.get(item.id);

      return {
        ...item,
        tableRow: getTrendDataTableRow(
          segment,
          getLiftComparisonValues(
            {
              segment: segment.data,
              comparisonSegment: getLiftBaselineSegment(
                this._bookmark.liftComparison,
                // this doesn't actually matter for non-time comparisons, but do it later for baseline and overall
                [segment.rowId],
                trendValueLookup,
                segment.timeComparisonData,
              ),
              liftType: this._bookmark.liftComparison?.type,
              projectTimezone: {
                timezone: projectDetails.projectTimeZone,
                utcOffset: projectDetails.utcOffset,
              },
            },
            (segmentDates): Record<string, number> =>
              // have to convert to iso date because of the utils we use for conversion
              // TODO(sam) change this dependency in a further refactor
              segmentDates.reduce(
                (acc, datePoint) => ({
                  ...acc,
                  [new Date(datePoint.timestamp).toISOString()]: datePoint.value,
                }),
                {},
              ),
          ),
        ),
      };
    });

    let segmentColumns = getSegmentColumnSpecs(this._bookmark);
    segmentColumns =
      segmentColumns.length > 0 ? segmentColumns : [{title: `Property`, segmentType: PropertyType.String}];

    const tableColumns = getTrendDataTableColumnGroups({
      segments: segmentColumns,
      row: trendDataRows[0],
      trendUnit: (this._bookmark.trend_unit as any) as TimeUnit,
      valueFormatter: (value, {isAverage}) => {
        const formatContext = isAverage ? TrendValueFormat.DataTableAverage : TrendValueFormat.Default;
        return formatTrendValue({value, trendChartType: this._bookmark.trendType, formatType: formatContext});
      },
    });

    this.segmentFilterController.loadSections([{items: legendSegmentItems, tableColumns}], {
      defaultNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelTrend].default,
      maxNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelTrend].max,
      tableSortConfig: this._bookmark.sorting,
    });
    this.segmentFilterController.loadFromSavedLegend(savedLegend);
    this.segmentFilterController.segmentationQueryLimit = REPORT_LIMITS[ReportName.Funnels].query;
    this.segmentFilterController.exportSegmentationQueryLimit = REPORT_LIMITS[ReportName.Funnels].csvExport;
  }
}
export const FunnelsTrendsChartEl = defineAndMakeJsxFactory<FunnelsTrendsChartJsxProps>(
  `mp-funnels-trends-chart`,
  FunnelsTrendsChart,
);
export const FunnelsTrendsChartReact = panel2react<FunnelsTrendsChartJsxProps>(
  `mp-funnels-trends-chart`,
  FunnelsTrendsChart,
);
